// utils/callApi.js
import { axiosInstance } from "./axios";
import { getAuthToken, refreshIfNeeded } from "./singletonToken";

function injectAuthHeaderIfMissing(req) {
  const request = { ...req, headers: { ...(req.headers || {}) } };
  const hasAuth = Object.keys(request.headers).some(
    (k) => k.toLowerCase() === "authorization"
  );
  if (!hasAuth) {
    const token = getAuthToken?.();
    if (token) request.headers.Authorization = `Bearer ${token}`;
  }
  return request;
}

function is401(err) {
  return !!(err && err.response && err.response.status === 401);
}

export const callApi = ({ request, config }) => {
  const {
    isMountedRef,
    setResults,
    updateMessageStatus,
    callback,
    abortSignal,
    logLevel = ["error"],
    resolve,
    reject,
    setLoadingState,
    setStatus,
  } = config;

  if (!request) return;

  const localController = !abortSignal ? new AbortController() : null;
  const signal = abortSignal ?? localController.signal;
  const sendOnce = (req) => axiosInstance({ ...req, signal });

  const ping = (v) => {
    try {
      if (setStatus) setStatus(v);
    } catch {}
  };

  const finishSuccess = (data, message = "Success") => {
    if (!isMountedRef?.()) return;
    setResults?.(data);
    setLoadingState?.(false);
    updateMessageStatus?.({
      requestUrl: request.url,
      status: "success",
      message,
      logLevel,
    });
    ping("success");
    resolve?.(data);
    callback?.(data);
  };

  const finishNoContent = () => {
    if (!isMountedRef?.()) return;
    setResults?.("No Content");
    setLoadingState?.(false);
    updateMessageStatus?.({
      requestUrl: request.url,
      status: "success",
      message: "No Content",
      logLevel,
    });
    ping(204);
    resolve?.(null);
    callback?.(null);
  };

  const finishError = (err) => {
    const msgRaw = err?.response?.data ?? err?.message ?? "Request error";
    const errorMessage =
      typeof msgRaw === "string" ? msgRaw : JSON.stringify(msgRaw);
    updateMessageStatus?.({
      requestUrl: request.url,
      status: "error",
      message: errorMessage,
      logLevel,
    });
    setLoadingState?.(false);
    ping(err?.response?.status ?? "error");
    reject?.(err);
  };

  setLoadingState?.(true);
  ping("loading");

  (async () => {
    try {
      await refreshIfNeeded({ reason: "pre-send" });
      const req1 = injectAuthHeaderIfMissing(request);
      const res1 = await sendOnce(req1);

      if (res1.status === 204) return finishNoContent();
      ping(res1.status);
      return finishSuccess(res1.data);
    } catch (err) {
      // *** abort check: silently ignore, no logging ***
      if (err?.name === "CanceledError") {
        return; // do nothing
      }

      if (is401(err)) {
        ping(401);
        const ok = await refreshIfNeeded({ force: true, reason: "401" });
        if (ok) {
          ping("retry");
          try {
            const req2 = injectAuthHeaderIfMissing(request);
            const res2 = await sendOnce(req2);
            if (res2.status === 204) return finishNoContent();
            ping(res2.status);
            return finishSuccess(res2.data);
          } catch (err2) {
            // *** abort check here too ***
            if (err2?.name === "CanceledError") {
              return;
            }
            return finishError(err2);
          }
        }
      }

      return finishError(err);
    }
  })();

  return () => {
    if (localController) localController.abort();
  };
};

export const shouldRequest = (requestNothingOnNull, requestFunctionValues) => {
  if (requestNothingOnNull) {
    const firstValue = requestFunctionValues[0];
    if (
      !requestFunctionValues.length ||
      firstValue === "" ||
      firstValue === null ||
      firstValue === undefined ||
      Number.isNaN(firstValue)
    ) {
      return false;
    }
  }
  return true;
};
